// ./src/components/features/EventGuests/GuestContactOverrides.tsx
//
// What a guest linked to a contact can have of its own (owner, 2026-11-02): details for this one event, and another contact.
// A guest's details are its contact's; a value here wins over them everywhere, emails included, so the section says when one is
// active and offers the way back. The contact's values are placeholders, never copied in. The rules: `utils/guestOverrides`.
// ⓘ New because it is a drawer's section with its own open state and rules, and GuestDrawer is already over 1,300 lines.
import React, { useState } from "react";
import { Alert, Button, Collapse, Group, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { PhoneField, TextField } from "@/components/generic/form";
import { activeGuestOverrides, GuestOverrideField } from "@/utils/guestOverrides";

type Values = Partial<Record<GuestOverrideField, string | null | undefined>>;

export interface GuestContactOverridesProps {
  /** What the form holds now, so typing or clearing shows at once. */
  values: Values;
  /** The contact's own values, or null while another contact has been picked and not saved. */
  contact: Values | null;
  onChange: React.ComponentProps<typeof TextField>["onChange"];
  onClear: () => void;
  errors: Partial<Record<GuestOverrideField, string | undefined>>;
  defaultPhoneCountry?: React.ComponentProps<typeof PhoneField>["defaultCountry"];
  changingContact: boolean;
  onToggleChangeContact: () => void;
  /** The contact picker, rendered while changing. */
  children?: React.ReactNode;
}

export const GuestContactOverrides: React.FC<GuestContactOverridesProps> = ({
  values,
  contact,
  onChange,
  onClear,
  errors,
  defaultPhoneCountry,
  changingContact,
  onToggleChangeContact,
  children,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const overridden = activeGuestOverrides(values, contact);
  // ⚠ A refusal opens the section: its message must not be shut away under a closed heading.
  const hasError = !!(errors.firstName || errors.lastName || errors.email || errors.phone);
  const fieldWords = overridden
    .map((field) =>
      field === "email"
        ? t("events:guests.overrideEmail", { defaultValue: "email" })
        : field === "phone"
          ? t("events:guests.overridePhone", { defaultValue: "phone" })
          : t("events:guests.overrideName", { defaultValue: "name" }),
    )
    .filter((word, index, all) => all.indexOf(word) === index)
    .join(", ");

  return (
    <>
      {overridden.length > 0 && (
        <Alert color="yellow">
          <Stack gap="xs" align="flex-start">
            <Text size="md">
              {t("events:guests.overrideActive", {
                defaultValue: "This event uses its own {{fields}}, not the contact's. Messages about this event go there too.",
                fields: fieldWords,
              })}
            </Text>
            <Button variant="light" size="md" onClick={onClear}>
              {t("events:guests.useContactDetails", { defaultValue: "Use the contact's details" })}
            </Button>
          </Stack>
        </Alert>
      )}

      <Group gap="md">
        <Button variant="subtle" size="md" px={0} onClick={() => setOpen((was) => !was)} aria-expanded={open || hasError}>
          {t("events:guests.forThisEvent", { defaultValue: "For this event only" })}
        </Button>
        <Button variant="subtle" size="md" px={0} onClick={onToggleChangeContact} aria-expanded={changingContact}>
          {t("events:guests.changeContact", { defaultValue: "Change contact" })}
        </Button>
      </Group>

      <Collapse in={open || hasError}>
        <Stack gap="sm">
          <Text size="md" c="dimmed">
            {t("events:guests.forThisEventHint", { defaultValue: "Leave a field empty to use the contact's. This does not change the contact." })}
          </Text>
          <Group gap="sm" grow align="flex-start">
            <TextField name="firstName" label={t("events:guests.firstName", { defaultValue: "First name" })} value={values.firstName ?? ""} onChange={onChange} placeholder={contact?.firstName ?? undefined} error={errors.firstName} />
            <TextField name="lastName" label={t("events:guests.lastName", { defaultValue: "Last name" })} value={values.lastName ?? ""} onChange={onChange} placeholder={contact?.lastName ?? undefined} error={errors.lastName} />
          </Group>
          <Group gap="sm" grow align="flex-start">
            <TextField name="email" label={t("events:guests.email", { defaultValue: "Email" })} value={values.email ?? ""} onChange={onChange} placeholder={contact?.email ?? undefined} error={errors.email} />
            <PhoneField name="phone" label={t("events:guests.phone", { defaultValue: "Phone" })} value={values.phone ?? ""} onChange={onChange} placeholder={contact?.phone ?? undefined} defaultCountry={defaultPhoneCountry} error={errors.phone} />
          </Group>
        </Stack>
      </Collapse>

      {children}
    </>
  );
};
